import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { NewsPost, NewsPostSummary, NewsContentSegment, parseNewsContent, newsTeaser, EventCategory, CATEGORY_LABELS } from '@radevents/shared';
import { buildNewsArticleSchema } from '../hooks/useNewsSchema';
import PublicHeader from '../components/Layout/PublicHeader';
import AppTopbarBrand from '../components/Layout/AppTopbarBrand';
import newsService from '../services/newsService';
import { trackNewsClick, trackOnce } from '../services/trackingService';
import { categoryColor, categoryLabel, Diamond } from './News';

import { SITE_ORIGIN } from '../config/siteOrigin';
import { formatDayLong } from '../utils/format';

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

/** Inline-Auszeichnung eines Absatzes: Links, fett, kursiv. */
const renderSegments = (segments: NewsContentSegment[]) =>
  segments.map((seg, i) => {
    const text = seg.bold ? <strong>{seg.text}</strong> : seg.italic ? <em>{seg.text}</em> : seg.text;
    return seg.url ? (
      <a key={i} href={seg.url} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', fontWeight: 600 }}>
        {text}
      </a>
    ) : (
      <React.Fragment key={i}>{text}</React.Fragment>
    );
  });

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // Zurück per Verlauf; bei einem Direktaufruf ohne Verlauf auf die News-Übersicht.
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/news');
  };
  const [post, setPost] = useState<NewsPost | null>(null);
  const [others, setOthers] = useState<NewsPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    newsService.getBySlug(slug)
      .then(p => { setPost(p); trackOnce('NEWS', slug, 'VIEW'); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // 5 reichen: 4 Karten plus ggf. der aktuelle Artikel, der herausgefiltert wird
    newsService.getAll(0, 5)
      .then(page => setOthers(page.content))
      .catch(() => {});
  }, [slug]);

  // Ohne Web-Share-API (Desktop-Browser) kopiert der Button nur den Link — das soll er auch sagen.
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const share = async () => {
    const url = `${SITE_ORIGIN}/news/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: post?.title, url }); } catch { /* abgebrochen */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopiert');
    }
  };

  const moreArticles = others.filter(o => o.slug !== slug).slice(0, 4);
  const canonical = `${SITE_ORIGIN}/news/${slug}`;

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      {post && (
        <Helmet>
          <title>{`${post.title} | Radsport Events`}</title>
          <meta name="description" content={newsTeaser(post)} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={newsTeaser(post)} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="article" />
          {post.coverImageUrl && <meta property="og:image" content={post.coverImageUrl} />}
          <meta name="twitter:card" content={post.coverImageUrl ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={newsTeaser(post)} />
          <script type="application/ld+json">{JSON.stringify(buildNewsArticleSchema(post))}</script>
        </Helmet>
      )}
      <style>{`
        /* Blocksatz nur auf breiten Spalten — auf Handys reißt er Löcher zwischen die Wörter. */
        @media (min-width: 641px) {
          .news-body--justified p,
          .news-body--justified li { text-align: justify; hyphens: auto; }
        }
        @media (max-width: 640px) {
          .news-article-container { padding-left: 20px !important; padding-right: 20px !important; }
          .news-article-hero { height: 240px !important; }
          .news-article-title { font-size: 28px !important; }
          .news-more-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="hide-mobile">
        <PublicHeader />
      </div>
      {/* Phone-only app top bar: Zurück und Logo wie auf Event-Seite und Profi-Rennen. */}
      <div className="app-topbar hide-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
        <AppTopbarBrand onBack={goBack} />
      </div>

      <div className="news-article-container" style={{ flex: 1, width: '100%', boxSizing: 'border-box', maxWidth: 760, margin: '0 auto', padding: '24px 40px 60px' }}>
        {loading ? (
          <div style={{ fontFamily: FONT_BODY, color: '#6A6A6A' }}>Lade Artikel…</div>
        ) : notFound || !post ? (
          <div style={{ padding: 50, textAlign: 'center', background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, fontFamily: FONT_BODY, color: '#6A6A6A' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22, color: '#2C2C2C', marginBottom: 8 }}>Artikel nicht gefunden</div>
            Dieser Artikel existiert nicht oder wurde entfernt.
            <div style={{ marginTop: 16 }}>
              <Link to="/news" style={{ fontWeight: 700, color: '#922C57', textDecoration: 'none' }}>‹ Zur News-Übersicht</Link>
            </div>
          </div>
        ) : (
          <>
            {/* Breadcrumb, nur Desktop: mobil führt der Zurück-Knopf oben zurück. */}
            <Link to="/news" className="hide-mobile" style={{ display: 'block', fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.06em', color: '#6A6A6A', textDecoration: 'none', marginBottom: 20 }}>
              ‹ NEWS / <span style={{ color: '#2C2C2C' }}>{categoryLabel(post.category)}</span>
            </Link>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 12.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: categoryColor(post.category) }}>
              <Diamond size={8} color={categoryColor(post.category)} />
              {categoryLabel(post.category)} · {formatDayLong(post.publishedAt)}
            </span>
            <h1 className="news-article-title" style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 38, lineHeight: 1.08, letterSpacing: '-0.015em', color: '#2C2C2C', margin: '14px 0 0' }}>
              {post.title}
            </h1>

            {/* Hero */}
            <div className="news-article-hero" style={{ position: 'relative', height: 400, borderRadius: 16, overflow: 'hidden', marginTop: 22, backgroundImage: post.coverImageUrl ? `url('${post.coverImageUrl}')` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#EEEBF5' }}>
              {post.photoCredit && (
                <span style={{ position: 'absolute', left: 0, bottom: 0, right: 0, padding: '12px 16px', fontFamily: FONT_BODY, fontSize: 12, color: '#EDE7FA', background: 'linear-gradient(0deg, rgba(38,38,38,0.72), transparent)' }}>
                  {post.photoCredit}
                </span>
              )}
            </div>

            {/* Fließtext */}
            <div className={post.justified ? 'news-body news-body--justified' : 'news-body'} style={{ marginTop: 26 }}>
              {parseNewsContent(post.content).map((block, i) => {
                switch (block.type) {
                  case 'heading':
                    return block.level === 2 ? (
                      <h2 key={i} style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 24, lineHeight: 1.2, color: '#2C2C2C', margin: '30px 0 10px' }}>
                        {renderSegments(block.segments)}
                      </h2>
                    ) : (
                      <h3 key={i} style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 19, lineHeight: 1.25, color: '#2C2C2C', margin: '24px 0 8px' }}>
                        {renderSegments(block.segments)}
                      </h3>
                    );
                  case 'quote':
                    return (
                      <blockquote key={i} style={{ margin: '0 0 18px', padding: '4px 0 4px 16px', borderLeft: '3px solid #922C57', fontFamily: FONT_BODY, fontSize: 17, lineHeight: 1.7, color: '#6A6A6A', fontStyle: 'italic' }}>
                        {renderSegments(block.segments)}
                      </blockquote>
                    );
                  case 'list': {
                    const ListTag = block.ordered ? 'ol' : 'ul';
                    return (
                      <ListTag key={i} style={{ margin: '0 0 18px', paddingLeft: 24, fontFamily: FONT_BODY, fontSize: 17, lineHeight: 1.7, color: '#4A4A4A' }}>
                        {block.items.map((item, j) => (
                          <li key={j} style={{ margin: '0 0 6px' }}>{renderSegments(item)}</li>
                        ))}
                      </ListTag>
                    );
                  }
                  default:
                    return (
                      <p key={i} style={{ fontFamily: FONT_BODY, fontSize: 17, lineHeight: 1.7, color: '#4A4A4A', margin: '0 0 18px' }}>
                        {renderSegments(block.segments)}
                      </p>
                    );
                }
              })}
            </div>

            {/* Verknüpfte Events: CTA vom Artikel direkt zur Veranstaltung. Mehrere sind der
                Normalfall — dieselbe Veranstaltung liegt bei uns nach Disziplin getrennt vor,
                deshalb steht die Kategorie an der Karte, sobald es mehr als eine gibt. */}
            {(post.linkedEvents ?? []).length > 0 && (
              <div style={{ marginTop: 30 }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9A9494', marginBottom: 10 }}>
                  {post.linkedEvents!.length === 1 ? 'Zum Event' : 'Zu den Events'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {post.linkedEvents!.map(ev => (
                    <Link
                      key={ev.id}
                      to={`/events/${ev.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, textDecoration: 'none', transition: 'border-color .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = categoryColor(ev.category); }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E1E1'; }}
                    >
                      <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 10, background: categoryColor(ev.category), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Diamond size={16} color="#fff" />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {post.linkedEvents!.length > 1 && (
                          <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: categoryColor(ev.category) }}>
                            {CATEGORY_LABELS[ev.category as EventCategory] ?? ev.category}
                          </div>
                        )}
                        <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 17, lineHeight: 1.2, color: '#2C2C2C', margin: '3px 0' }}>{ev.title}</div>
                        <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6A6A6A' }}>
                          {new Date(ev.eventDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                          {ev.endDate ? ` – ${new Date(ev.endDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}` : ''}
                          {' · '}{ev.city}
                        </div>
                      </div>
                      <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 20, color: categoryColor(ev.category), flexShrink: 0 }}>›</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Autor-/Aktionsleiste */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginTop: 14, paddingTop: 20, borderTop: '1px solid #E5E1E1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: '#922C57', transform: 'rotate(45deg)' }} />
                <div>
                  <div style={{ fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: '#9A9494' }}>
                    {post.publishedAt
                      ? `Veröffentlicht am ${new Date(post.publishedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} von:`
                      : 'Veröffentlicht von:'}
                  </div>
                  <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: '#2C2C2C' }}>{post.authorName}</div>
                </div>
              </div>
              <button
                onClick={share}
                title={canNativeShare
                  ? 'Artikel teilen – öffnet das Teilen-Menü deines Geräts (WhatsApp, Mail, …)'
                  : 'Link zu diesem Artikel in die Zwischenablage kopieren'}
                aria-label={canNativeShare ? 'Artikel teilen' : 'Artikel-Link kopieren'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', borderRadius: 999, padding: '10px 18px', fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, background: '#fff', color: '#2C2C2C', border: '1px solid #E5E1E1' }}
              >
                {canNativeShare ? '↗ Teilen' : '↗ Link kopieren'}
              </button>
            </div>

            {/* Weitere Artikel */}
            {moreArticles.length > 0 && (
              <>
                <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.12em', color: '#9A9494', margin: '40px 0 14px' }}>WEITERE ARTIKEL</div>
                <div className="news-more-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {moreArticles.map(n => (
                    <Link
                      key={n.id}
                      to={`/news/${n.slug}`}
                      onClick={() => trackNewsClick(n.slug, 'news-related')}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#2C2C2C'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E1E1'; }}
                      style={{ display: 'flex', gap: 13, background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 12, textDecoration: 'none', transition: 'border-color .15s' }}
                    >
                      <div style={{ width: 82, height: 82, flexShrink: 0, borderRadius: 10, backgroundImage: n.coverImageUrl ? `url('${n.coverImageUrl}')` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#EEEBF5' }} />
                      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 11.5, letterSpacing: '0.05em', color: categoryColor(n.category) }}>
                          {categoryLabel(n.category)} · {formatDayLong(n.publishedAt)}
                        </span>
                        <h4 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 15, lineHeight: 1.15, color: '#2C2C2C', margin: '6px 0 0' }}>{n.title}</h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

    </div>
  );
}
